/**
 * `POST /api/explain`.
 *
 * Plain text, streamed. The response is prose the player asked for by tapping, so the
 * useful behaviour is to start rendering it as it arrives rather than to wait for a
 * paragraph that takes a second to finish.
 *
 * ## The debug trailer
 *
 * A request that asked to see its prompt gets one extra thing after the prose: the debug
 * echo as JSON, behind `EXPLAIN_DEBUG_MARKER` — mirrored by hand from
 * `packages/server/src/routes/schemas.ts`, like every other shape in this folder.
 *
 * It is stripped here and never yielded. The sheet renders what this generator produces,
 * and a stray `[[hc-debug]]{"model":…` flashing at the end of every explanation would be a
 * worse bug than having no diagnostics at all. A response with no trailer — which is every
 * response while the setting is off — streams exactly as it always did.
 */

import {
  parseDebugPayload,
  postJson,
  type ApiOptions,
  type ExplainRequest,
  type LlmDebugPayload,
} from './types.js';

/**
 * Where the prose stops and the echo starts.
 *
 * The server's `EXPLAIN_DEBUG_MARKER`, written out here rather than imported because the
 * client cannot import from `@hc/server`. A change is a change to two files.
 */
export const EXPLAIN_DEBUG_MARKER = '\n[[hc-debug]]';

/**
 * How much of the tail is held back while the stream is open.
 *
 * Chunk boundaries land anywhere, so without this a marker split across two reads would
 * never be found and its JSON would be rendered as prose instead. Thirteen characters of
 * latency at the very end of a paragraph is not perceptible.
 */
const MARKER_LOOKBACK = EXPLAIN_DEBUG_MARKER.length;

/** The trailer as a payload, or `null` for anything that does not parse as one. */
function readTrailer(trailer: string): LlmDebugPayload | null {
  const text = trailer.slice(EXPLAIN_DEBUG_MARKER.length).trim();
  if (text === '') return null;
  try {
    const parsed: unknown = JSON.parse(text);
    // The echo bare, which is what the server writes. `{ debug: … }` is accepted as well,
    // so this agrees with the other two routes if that shape is ever unified.
    const record = parsed as { debug?: unknown };
    return parseDebugPayload(record?.debug ?? parsed);
  } catch {
    return null;
  }
}

export interface ExplainOptions extends ApiOptions {
  /** Called with each chunk of text as it arrives. */
  onDelta?: (delta: string) => void;
  /** Called if the response carried a debug trailer. */
  onDebug?: (debug: LlmDebugPayload) => void;
}

/** Stream an explanation, yielding prose as it arrives and stripping any debug trailer. */
export async function* streamExplanation(
  request: ExplainRequest,
  options?: ExplainOptions,
): AsyncGenerator<string> {
  const response = await postJson('/api/explain', request, options, 'text/plain');

  /** Everything from the marker onwards. Non-null once the trailer has begun. */
  let trailer: string | null = null;
  /** Prose read but not yet yielded: the lookback window, or a whole unmarked body. */
  let buffer = '';

  function absorb(text: string): string {
    if (trailer !== null) {
      trailer += text;
      return '';
    }
    buffer += text;
    const at = buffer.indexOf(EXPLAIN_DEBUG_MARKER);
    if (at >= 0) {
      const head = buffer.slice(0, at);
      trailer = buffer.slice(at);
      buffer = '';
      return head;
    }
    if (buffer.length <= MARKER_LOOKBACK) return '';
    const emit = buffer.slice(0, buffer.length - MARKER_LOOKBACK);
    buffer = buffer.slice(buffer.length - MARKER_LOOKBACK);
    return emit;
  }

  function finish(): string {
    if (trailer === null) {
      const rest = buffer;
      buffer = '';
      return rest;
    }
    const debug = readTrailer(trailer);
    if (debug) options?.onDebug?.(debug);
    return '';
  }

  const body = response.body;
  if (!body) {
    const emitted = absorb(await response.text());
    if (emitted !== '') yield emitted;
    const tail = finish();
    if (tail !== '') yield tail;
    return;
  }

  const reader = body.getReader();
  const decoder = new TextDecoder();
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      // `{ stream: true }` keeps a multi-byte character split across two chunks intact —
      // which matters here, because the prose is full of ♭ and ♯.
      const emitted = absorb(decoder.decode(value, { stream: true }));
      if (emitted !== '') yield emitted;
    }
    const decoded = absorb(decoder.decode());
    if (decoded !== '') yield decoded;
    const tail = finish();
    if (tail !== '') yield tail;
  } finally {
    reader.releaseLock();
  }
}

/** Collect a whole explanation, reporting text as it streams. */
export async function explain(request: ExplainRequest, options?: ExplainOptions): Promise<string> {
  let text = '';
  for await (const delta of streamExplanation(request, options)) {
    text += delta;
    options?.onDelta?.(delta);
  }
  return text;
}
